import { TIMEZONE } from '../../config';

type DateParts = {
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
    second: number;
};

const APP_TIMEZONE = TIMEZONE || 'Pacific/Honolulu';
const DATE_TIME_LOCAL_REGEX = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;

const pad = (n: number, w: number) => String(n).padStart(w, '0');

function parseDate(dateTime: string | Date): Date | null {
    if (!dateTime) return null;
    const d = new Date(dateTime);
    return Number.isNaN(d.getTime()) ? null : d;
}

export function getPartsInTz(date: Date, tz: string): DateParts {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    }).formatToParts(date);
    const v = Object.fromEntries(parts.map((p) => [p.type, p.value]));
    return {
        year: Number(v.year),
        month: Number(v.month),
        day: Number(v.day),
        hour: Number(v.hour),
        minute: Number(v.minute),
        second: Number(v.second),
    };
}

const DATE_TIME_OPTS: Intl.DateTimeFormatOptions = {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
};

function formatDateTimeCore(dateTime: string | Date, timeZone?: string): string {
    const date = parseDate(dateTime);
    if (!date) return '-';
    return date.toLocaleString('en-US', { ...DATE_TIME_OPTS, ...(timeZone && { timeZone }) });
}

export function formatDateTimeInVenueTz(dateTime: string | Date): string {
    return formatDateTimeCore(dateTime, APP_TIMEZONE);
}

export function formatTimeInVenueTz(
    dateTime: string | Date,
    locale = 'en-US',
    hour12?: boolean,
): string {
    const date = parseDate(dateTime);
    if (!date) return '';
    return date.toLocaleTimeString(locale, {
        timeZone: APP_TIMEZONE,
        hour: 'numeric',
        minute: '2-digit',
        ...(hour12 !== undefined && { hour12 }),
    });
}

export function formatDateTimeLocal(dateTime: string | Date): string {
    const date = parseDate(dateTime);
    if (!date) return '';
    const p = getPartsInTz(date, APP_TIMEZONE);
    return `${pad(p.year, 4)}-${pad(p.month, 2)}-${pad(p.day, 2)}T${pad(p.hour, 2)}:${pad(p.minute, 2)}`;
}

/**
 * Returns a Date representing noon on the given calendar day in the specified timezone.
 * Used for building calendar grids in venue timezone.
 */
export function getDateAtNoonInTimezone(
    year: number,
    month: number,
    day: number,
    tz: string = APP_TIMEZONE,
): Date {
    const target: DateParts = {
        year,
        month,
        day,
        hour: 12,
        minute: 0,
        second: 0,
    };
    let utc = new Date(
        Date.UTC(target.year, target.month - 1, target.day, target.hour, target.minute, 0),
    );
    for (let i = 0; i < 3; i++) {
        const actual = getPartsInTz(utc, tz);
        const targetMs = Date.UTC(
            target.year,
            target.month - 1,
            target.day,
            target.hour,
            target.minute,
            0,
        );
        const actualMs = Date.UTC(
            actual.year,
            actual.month - 1,
            actual.day,
            actual.hour,
            actual.minute,
            0,
        );
        const diff = targetMs - actualMs;
        if (diff === 0) break;
        utc = new Date(utc.getTime() + diff);
    }
    return utc;
}

/**
 * Returns a date key (YYYY-MM-DD) for grouping/filtering in the given timezone.
 * Use this instead of toDateString() when timeslots should be grouped by venue date.
 */
export function getDateKeyInTimezone(dateTime: string | Date, tz: string = APP_TIMEZONE): string {
    const date = parseDate(dateTime);
    if (!date) return '';
    const p = getPartsInTz(date, tz);
    return `${pad(p.year, 4)}-${pad(p.month, 2)}-${pad(p.day, 2)}`;
}

export function parseDateTimeLocal(input: string): string {
    const m = input?.match(DATE_TIME_LOCAL_REGEX);
    if (!m) return '';
    const [, y, mo, d, h, mi] = m;
    const target: DateParts = {
        year: Number(y),
        month: Number(mo),
        day: Number(d),
        hour: Number(h),
        minute: Number(mi),
        second: 0,
    };

    let utc = new Date(
        Date.UTC(target.year, target.month - 1, target.day, target.hour, target.minute, 0),
    );
    for (let i = 0; i < 3; i++) {
        const actual = getPartsInTz(utc, APP_TIMEZONE);
        const targetMs = Date.UTC(
            target.year,
            target.month - 1,
            target.day,
            target.hour,
            target.minute,
            0,
        );
        const actualMs = Date.UTC(
            actual.year,
            actual.month - 1,
            actual.day,
            actual.hour,
            actual.minute,
            0,
        );
        const diff = targetMs - actualMs;
        if (diff === 0) break;
        utc = new Date(utc.getTime() + diff);
    }
    return utc.toISOString();
}
